import axios from 'axios';
import {
	setAccessToken,
	setUserEmail,
	setUserId,
	setUserRole,
} from '../Utils/AuthUtil';
import {
	FeatureLimit,
	LoginResponse,
	NewEndUser,
	ResetPassword,
} from '../types';

const baseUrl = process.env.REACT_APP_UMS_API_URL || '';

const password_host = process.env.REACT_APP_RESET_PASSWORD_HOST;

export async function loginUser(
	email: string,
	password: string,
): Promise<LoginResponse | undefined> {
	const body = `client_id=tourism-client&client_secret=tourism-secret&grant_type=password&username=${email}&password=${password}`;
	try {
		const response = await axios.post(`${baseUrl}/oauth/token`, body);
		if (response.status === 200) {
			setAccessToken(response.data.access_token);
			setUserRole(response.data.role);
			setUserId(response.data.id);
			setUserEmail(response.data.name);

			return {
				data: {
					status: response.status,
					role: response.data.role,
					userId: response.data.id,
					token: response.data.access_token,
				},
				error: false,
				message: '',
			};
		} else {
			throw { response: { status: 400 } };
		}
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return {
				error: true,
				message: error.message,
				data: {
					status: error.response?.status || 0,
					role: 0,
					userId: '',
					token: '',
				},
			};
		} else {
			console.error('Something went wrong', error);
		}
	}
}

export async function fetchAllAdmins(token: string) {
	try {
		const response = await axios.get(`${baseUrl}/admin/`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function fetchUser(id: string, token: string) {
	try {
		const response = await axios.get(`${baseUrl}/admin/${id}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function fetchAdminUser(id: string, token: string) {
	try {
		const response = await axios.get(`${baseUrl}/admin/${id}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function resetUserPassword(body: ResetPassword) {
	try {
		const response = await axios.post(
			`${baseUrl}/reset/resetPassword`,
			body,
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function generateResetUserPasswordLink(email: string) {
	const body = {
		email: email,
		host: password_host,
	};

	try {
		const response = await axios.post(
			`${baseUrl}/reset/request-resetPassword`,
			body,
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function updateAdmin(
	id: string,
	token: string,
	body: { userType: string },
) {
	try {
		const response = await axios.put(`${baseUrl}/admin/${id}`, body, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.response?.data.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function deleteAdmin(id: string, token: string) {
	try {
		const response = await axios.delete(`${baseUrl}/admin/${id}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function createNewUserAccount(body: NewEndUser) {
	try {
		const response = await axios.post(`${baseUrl}/adminSignUp`, body);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.response?.data.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function fetchGuestUserLimits(id: string, token: string) {
	try {
		const response = await axios.get(`${baseUrl}/admin/${id}/limit`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data.content;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function updateGuestUserLimits(
	id: string,
	token: string,
	data: FeatureLimit,
	isIncreasingLimit: boolean = true,
) {
	try {
		const type = isIncreasingLimit ? 'increase' : 'decrease';
		const response = await axios.put(
			`${baseUrl}/admin/${id}/limit/${type}`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);
		return response.data.content;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function onAddClickStat(
	id: string,
	item: string,
	loggedIn: boolean,
	location: string,
	email: string,
	visitorId: string,
) {
	try {
		const body = {
			userId: id,
			clickedItem: item,
			userLoggedIn: loggedIn,
			clickedLocation: location,
			email: email,
			visitorId: visitorId,
		};

		const response = await axios.post(`${baseUrl}/addStat`, body);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.response?.data.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}

export async function fetchStatsForDashboard(token: string) {
	try {
		const response = await axios.get(`${baseUrl}/stats/dashboard`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data.content;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			return { error: true, message: error.message };
		} else {
			console.error('Something went wrong', error);
			return { error: true, message: error };
		}
	}
}
